import { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { usePlayer } from "@/context/PlayerContext";

// Fetches a signed stream URL once `active` and reports a qualified view after enough real watching.
export function useVideoView(video, active = true) {
  const { request } = useProfile();
  const { pause: pauseMusic } = usePlayer();
  const [src, setSrc] = useState(null);
  const view = useRef({ sessionId: null, need: 10, watched: 0, last: 0, sent: false });
  const retried = useRef(false);
  const resumeAt = useRef(0);

  const starting = useRef(false);
  const [viewed, setViewed] = useState(false);

  const startSession = useCallback(async (refresh = false) => {
    if (starting.current) return;
    starting.current = true;
    try {
      const s = await request("post", `/videos/${video.id}/watch-session`);
      // On a token refresh keep the original watch session so the view still qualifies; only swap the URL.
      if (!refresh) view.current = { ...view.current, sessionId: s.session_id, need: s.qualify_after_seconds };
      setSrc(`${BACKEND_URL}${s.stream_url}${refresh ? `&r=${Date.now()}` : ""}`);
    } finally { starting.current = false; }
  }, [video.id, request]);

  useEffect(() => { if (active && !src) startSession().catch(() => {}); }, [active, src, startSession]);

  const onTimeUpdate = (e) => {
    const el = e.currentTarget, v = view.current;
    const delta = el.currentTime - v.last;
    if (delta > 0 && delta < 1.5) v.watched += delta;
    v.last = el.currentTime;
    if (!v.sent && v.sessionId && v.watched >= v.need) {
      v.sent = true;
      request("post", `/views/${v.sessionId}/qualify`, { listened_seconds: v.watched }).then((r) => r.qualified && setViewed(true)).catch(() => {});
    }
  };

  const onSeeked = (e) => { view.current.last = e.currentTarget.currentTime; };
  // Refresh an expired stream token once per load; don't loop on undecodable files.
  const onError = () => {
    if (src && !retried.current) { retried.current = true; resumeAt.current = view.current.last; startSession(true).catch(() => {}); }
  };
  // After a token refresh, continue from where playback stopped and allow future refreshes.
  const onLoadedMetadata = (e) => {
    if (resumeAt.current) { e.currentTarget.currentTime = resumeAt.current; view.current.last = resumeAt.current; resumeAt.current = 0; }
    retried.current = false;
  };

  return { src, viewed, onTimeUpdate, onLoadedMetadata, onSeeked, onError, onPlay: pauseMusic };
}
